// components/secure/SignatureApprovalsPanel.js — owner decision O5 (audit 2026-10).
// Signature requests issued by anyone other than the managing broker or an admin wait here
// for approval. The managing broker/admin approves (it is emailed at once, as the person
// who asked) or rejects with a reason; everyone else sees the state of their own requests.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const BADGE = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
};

const SignatureApprovalsPanel = () => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        api.get('/api/forms-engine/send-approvals')
            .then((d) => { setData(d); setError(''); })
            .catch((e) => setError(e.message || 'Could not load signature requests.'));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const act = async (r, verb) => {
        let body = {};
        if (verb === 'reject') {
            const reason = window.prompt(`Why is "${r.title}" not being sent? (the person who asked sees this)`);
            if (reason === null) return;
            body = { reason };
        }
        setBusy(r.approval_id);
        try {
            const out = await api.post(`/api/forms-engine/send-approvals/${r.approval_id}/${verb}`, body);
            setNotice(out.message || 'Done.');
            load();
        } catch (e) { setError(e.message); } finally { setBusy(null); }
    };

    if (!data) return error ? <p className="text-red-600 text-sm mb-6">{error}</p> : null;
    const rows = data.requests || [];
    if (!data.can_approve && rows.length === 0) return null;

    return (
        <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-4 py-3 border-b">
                <h2 className="font-semibold text-slate-800">Signature requests awaiting approval</h2>
                <p className="text-xs text-slate-500">
                    {data.can_approve
                        ? 'Requests from licensees and staff go out only once you approve them. Approving emails the signing links at once.'
                        : 'Your signature requests are sent once the managing broker approves them.'}
                </p>
            </div>
            {error && <p className="px-4 py-2 text-sm text-red-600">{error}</p>}
            {notice && <p className="px-4 py-2 text-sm text-green-700">{notice}</p>}
            {rows.length === 0 ? <p className="px-4 py-3 text-sm text-slate-500">Nothing waiting.</p> : (
                <div className="p-3"><DataList rows={rows} rowKey="approval_id" csvName="signature-approvals" defaultSort={{ key: 'created_at', dir: 'desc' }}
                    filters={[{ key: 'status', label: 'Status' }]}
                    columns={[
                        { key: 'title', label: 'Form', value: (r) => `${r.title} #${r.instance_id}`, sortValue: (r) => r.title, render: (r) => <>{r.title} <span className="text-slate-400">#{r.instance_id}</span></> },
                        { key: 'for', label: 'For', value: (r) => r.client_name || `${r.subject_type || 'client'} #${r.subject_id || ''}` },
                        { key: 'requested_by_name', label: 'Asked by', value: (r) => r.requested_by_name || 'Automation',
                            render: (r) => <>{r.requested_by_name || 'Automation'}<div className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</div></> },
                        { key: 'created_at', label: 'Asked', type: 'date' },
                        { key: 'status', label: 'Status',
                            render: (r) => <>
                                <span className={`px-2 py-0.5 rounded text-xs ${BADGE[r.status] || ''}`}>{r.status}</span>
                                {r.decision_note && <div className="text-xs text-slate-500 mt-1">{r.decision_note}</div>}
                                {r.result_message && <div className="text-xs text-slate-500 mt-1">{r.result_message}</div>}</> },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (r) => (
                            <span className="whitespace-nowrap">
                                {data.can_approve && r.status === 'pending' && (<>
                                    <button disabled={busy === r.approval_id} onClick={() => act(r, 'approve')}
                                        className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-indigo-300 mr-2">
                                        Approve &amp; send</button>
                                    <button disabled={busy === r.approval_id} onClick={() => act(r, 'reject')}
                                        className="px-3 py-1 text-xs text-red-700 border border-red-300 rounded hover:bg-red-50">
                                        Reject</button>
                                </>)}
                            </span>) },
                    ]} /></div>
            )}
        </div>
    );
};

export default SignatureApprovalsPanel;
